import React, { FC } from "react";

import { Spinner } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { Navigate, useParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import TasksService from "@/services/TasksService";

import { useActiveView } from "@/hooks/useActiveView";

import { ErrorLayout } from "@/components/templates";

import { homePath } from "@/utils/UHomePath";

import { taskKeys } from "@/constants/CQueryKeys";

import { TDetailedTask } from "@/types";

/**
 * /view/create was a page of its own; the view form now opens in a
 * modal over the list. Anything still pointing at the old address (a
 * bookmark, an older build of the sidebar) lands on the list with the
 * modal open.
 */
export const ViewCreateRedirect: FC = () => (
	<Navigate to="/views?new=1" replace />
);

/**
 * /tasks/:taskId - the flat address the search, the project page and
 * older links use. The home page shows a task in its panel, under a
 * view - the last one visited, or the first - so the address is
 * rewritten to the home page's shape. A component rather than a route
 * loader: on a direct visit a loader would run before the auth boot has
 * set the API token.
 */
export const TaskRedirect: FC = () => {
	const { taskId } = useParams<{ taskId: string }>();
	const id = Number(taskId);
	const { data, error } = useQuery<TDetailedTask>({
		queryKey: taskKeys.detail(id),
		queryFn: () => TasksService.fetchTask(id),
		enabled: Number.isInteger(id) && id > 0,
	});
	// The panel lives under a view: without one there is no address to
	// send the task to.
	const { view } = useActiveView();

	if (error || !Number.isInteger(id) || id <= 0)
		return (
			<ErrorLayout
				title={t("errors.notFound")}
				text={t("tasks.notFound")}
			/>
		);
	if (!data || !view) return <Spinner className="mx-auto my-8" />;
	return (
		<Navigate
			to={homePath({
				viewId: view.id,
				projectId: undefined,
				taskId: id,
			})}
			replace
		/>
	);
};
